import * as React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import { debounce } from "@mui/material/utils";
import {useNavigate} from "react-router-dom"
import WallPaper from '../assets/WallPaper.jpg';
import './SearchBox.css';
import InputAdornment from "@mui/material/InputAdornment";

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}

interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
}

const autocompleteService = { current: null };

// Search Box that auto completes based on the user's search query using Google Maps AutoComplete API
export default function SearchBox() {
  const [value, setValue] = React.useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
  let navigate = useNavigate();

  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        400
      ),
    []
  );

  // Fetches autocomplete results from Google Maps API
  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions([]); // Don't show any options when input is empty
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const handleEnter = (evt:any) => {
    if(evt.keyCode === 13)
    {
      navigate('/Explore', { state: { SelectedValue: value } });
    }}

  return (
    <>
    <img src={WallPaper} alt="Wallpaper" className="wallpaper" />
    <div className="search-container">
      <Autocomplete
        id="home-search-box"
        sx={{ 
          width: "55vw", 
          minWidth: 300, 
          maxWidth: 800,
          margin: '0 auto',
          '& .MuiOutlinedInput-root': {
            padding: '4px 16px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            borderRadius: '100px',
            transition: 'all 0.2s ease-in-out',
          }
        }}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        onKeyDown={handleEnter}
        noOptionsText="No results"
        // Only show the suggestions dropdown when there is input text and available options
        open={inputValue !== "" && options.length > 0}
        popupIcon={null}
        onChange={(event: any, newValue: PlaceType | null) => {
          setOptions(newValue ? [newValue, ...options] : options);
          setValue(newValue);
          if(newValue !== null){
            navigate('/Explore', { state: { SelectedValue: newValue } });
          }
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Where are you headed?"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: 26 }} />
                </InputAdornment>
              ),
              style: { 
                borderRadius: 100,
                backgroundColor: 'white', 
                height: '56px',
                paddingLeft: '16px',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused': {
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.25)',
                  outline: 'none',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                    boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.5)',
                    borderRadius: '100px',
                  }
                },
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }
              },
              // Hide the floating label since we're using placeholder text instead
              '& .MuiInputLabel-root': {
                display: 'none',
              },
              '& .MuiInputBase-input::placeholder': {
                opacity: 1,
                color: 'rgba(0, 0, 0, 0.7)',
                fontWeight: 400,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          />
        )}
        renderOption={(props, option) => {
          const matches =
            option.structured_formatting.main_text_matched_substrings || [];

          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match: any) => [
              match.offset,
              match.offset + match.length,
            ])
          );

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: "flex", width: 44 }}>
                  <LocationOnIcon sx={{ color: "text.secondary" }} />
                </Grid>
                <Grid
                  item
                  sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
                >
                  {parts.map((part, index) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                    >
                      {part.text}
                    </Box>
                  ))}
                  <Typography variant="body2" color="text.secondary">
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </div>
    </>
  );
}
